import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  CheckCircle,
  CreditCard,
  Building,
  Gift,
  ArrowRight,
  TrendingUp,
  Users,
  Sparkles,
  Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { organizationInfo } from '../data/mock';

const Donate = () => {
  // Donation action schema
  const donateSchema = {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    "name": "Donate to Mentors in Motion",
    "description": "Support youth mentoring and tutoring programs in St. Louis",
    "recipient": {
      "@type": "NonprofitOrganization",
      "name": "Mentors in Motion",
      "url": "https://mimstl.org"
    },
    "potentialAction": {
      "@type": "DonateAction",
      "target": "https://mimstl.org/donate"
    }
  };

  useEffect(() => {
    // Load Donorbox script
    const script = document.createElement('script');
    script.src = 'https://donorbox.org/widget.js';
    script.setAttribute('paypalExpress', 'false');
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://donorbox.org/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div>
      <SEO 
        title="Donate - Support Youth in St. Louis"
        description="Donate to Mentors in Motion. Help empower St. Louis youth through tutoring and mentoring. Tax-deductible gifts make a difference."
        keywords="donate youth nonprofit St. Louis, support youth mentoring, charitable giving Missouri, tax deductible donation nonprofit, help at-risk youth"
        canonicalUrl="/donate"
        structuredData={donateSchema}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Donate to Empower
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Your generosity can change a child's trajectory. When you give to Mentors in Motion, you provide scholars with consistent tutoring, caring mentors, and creative learning experiences that improve reading and math skills, boost school attendance, increase engagement, and build confidence. Your investment helps young people discover their talents, see real pathways to college, careers, and entrepreneurship, and grow into leaders who strengthen their families and communities.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Donorbox Embedded Form */}
            <div>
              <Card className="border-0 shadow-xl overflow-hidden">
                <CardHeader className="bg-red-600 text-white">
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <Heart className="w-6 h-6" />
                    Make a Donation
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Donorbox Embed */}
                  <iframe 
                    src="https://donorbox.org/embed/mentors-in-motion?default_interval=o"
                    name="donorbox" 
                    allowpaymentrequest="allowpaymentrequest" 
                    seamless="seamless" 
                    frameBorder="0" 
                    scrolling="no" 
                    height="900" 
                    width="100%" 
                    style={{ 
                      maxWidth: '500px', 
                      minWidth: '250px', 
                      maxHeight: 'none !important',
                      margin: '0 auto',
                      display: 'block'
                    }}
                    allow="payment"
                    title="Donorbox Donation Form"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Why Donate Section */}
            <div className="space-y-8">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Why Donate to Mentors in Motion?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    In too many St. Louis classrooms, brilliant young people are being left behind simply because they never got the right support at the right time. When a child struggles with reading or math year after year, it quietly shapes the rest of their life limiting the classes they can take, the schools they can attend, the jobs they can get, and the wealth they can build.
                  </p>
                  
                  <p className="text-gray-700 leading-relaxed font-semibold text-red-600">
                    Mentors in Motion steps directly into that gap.
                  </p>
                  
                  <p className="text-gray-700 leading-relaxed">
                    We combine mentoring, tutoring, and creative learning so scholars don't just catch up in school, they begin to see themselves as capable, talented, and essential to the future of their communities. Our programs are embedded in schools, grounded in long-term relationships, and designed by leaders who reflect the neighborhoods we serve.
                  </p>
                  
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <p className="text-gray-800 font-semibold mb-4">
                      When you donate to Mentors in Motion, you are not just funding programs, you are investing in outcomes:
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <TrendingUp className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Stronger academics</span>
                          <span className="text-gray-600"> – Scholars grow in literacy and mathematics, improving grades, test performance, and readiness for graduation.</span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Users className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Stronger support systems</span>
                          <span className="text-gray-600"> – Young people gain consistent, caring adults who walk with them through critical years, not just a single season.</span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Sparkles className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Stronger identities</span>
                          <span className="text-gray-600"> – Creative tracks in music, media, fashion, cosmetology, podcasting, and gaming help scholars discover their gifts and see real pathways to college, careers, and entrepreneurship.</span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Home className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Stronger communities</span>
                          <span className="text-gray-600"> – As scholars succeed, they lift their families and neighborhoods, becoming the next generation of mentors, business owners, and community leaders.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    Philanthropy has the power to change the story for an entire city when it backs trusted, community-rooted organizations already doing the work. That is what your gift to Mentors in Motion represents: a vote of confidence in young people who have been underestimated for far too long and a commitment to give them every opportunity to thrive.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Other Ways to Give - Full Width Below */}
          <Card className="mt-12 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Other Ways to Give</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Mail a Check</h4>
                    <p className="text-gray-600 text-sm">
                      Make checks payable to "Mentors in Motion" and mail to:<br />
                      {organizationInfo.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Donor-Advised Funds & Stock Gifts</h4>
                    <p className="text-gray-600 text-sm">
                      Contact us at {organizationInfo.email} for information on donating through DAFs or securities.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Corporate Matching Gifts</h4>
                    <p className="text-gray-600 text-sm">
                      Many employers match charitable contributions. Check with your HR department to double your impact!
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thank You Section */}
          <div className="mt-12 bg-red-50 p-8 rounded-2xl text-center max-w-2xl mx-auto">
            <Heart className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h4 className="font-semibold text-gray-900 text-xl mb-2">We Appreciate You</h4>
            <p className="text-gray-600">
              Thank you for investing in our scholars. Because of your generosity, young people in St. Louis are gaining the skills, confidence, and support they need to change the trajectory of their lives and their communities. We are grateful to have you as part of the Mentors in Motion family.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Prefer to Give Your Time?
          </h2>
          <p className="text-gray-600 mb-6">
            Volunteering is just as valuable as financial support.
          </p>
          <Link to="/get-involved">
            <Button variant="outline" className="border-gray-300 hover:bg-gray-100">
              Explore Volunteer Opportunities
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Donate;
